/**
 * Resolve where a notification click should go, and whether the user may open it.
 */

function normalizeRoute(route) {
  if (!route) return null;
  return String(route)
    .trim()
    .replace(/^\//, "")
    .replace(/\.vue$/i, "");
}

function hasAccess(rights, routeName, needEdit = false) {
  if (!routeName || !rights) return false;
  const r = rights[routeName];
  if (!r) return false;
  if (needEdit) return !!(r.edit || r.view);
  return !!(r.view || r.edit || r.add);
}

function firstAllowed(rights, candidates, needEdit = false) {
  for (const name of candidates) {
    if (hasAccess(rights, name, needEdit)) return name;
  }
  return null;
}

function entityId(notification) {
  if (!notification) return null;
  const raw =
    notification.localID ??
    notification.localId ??
    notification.entityID ??
    notification.entityId ??
    notification.refID ??
    notification.refId ??
    notification.sID ??
    notification.tID ??
    null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

/**
 * Map API / legacy route names + titles to the correct app edit/list route.
 */
export function resolveNotificationNavigation(notification, rights = {}) {
  if (!notification) {
    return { ok: false, reason: "empty" };
  }

  const title = String(notification.title || "").toLowerCase();
  const type = String(notification.type || "").toLowerCase();
  const body = String(notification.body || "").toLowerCase();
  const text = `${title} ${body}`;
  const id = entityId(notification);
  let routeName = normalizeRoute(notification.route);

  // Infer route from title/type when API route is missing or is a generic list
  const inferred = inferRouteFromContent({ title, type, text, id });
  if (!routeName) {
    routeName = inferred;
  } else {
    // Upgrade list routes to edit forms when we have an entity id
    routeName = upgradeListToEdit(routeName, id, { title, type, text }) || routeName;
  }

  if (!routeName) {
    return { ok: false, reason: "no-route" };
  }

  // Alias candidates (rights may be on either name)
  const aliases = routeAliases(routeName);
  const rightsRoute = firstAllowed(rights, aliases, !!id);
  if (!rightsRoute) {
    return {
      ok: false,
      reason: "no-rights",
      routeName,
      aliases,
    };
  }

  // Navigate to the edit/form route even if rights live on a list alias
  const navigateRoute = navigationRouteFor(routeName, rightsRoute, id);
  const { params, query } = buildParams(navigateRoute, id);

  return {
    ok: true,
    name: navigateRoute,
    params,
    query,
    id,
    aliases,
  };
}

/** Prefer concrete edit form route when we have an entity id. */
function navigationRouteFor(preferredRoute, rightsRoute, id) {
  if (!id) return rightsRoute || preferredRoute;

  const editPrefer = {
    applicants: "staff-application",
    "staff-application": "staff-application",
    "manage-staffgrid": "manage-staff",
    "manage-staff": "manage-staff",
    "manage-studentgrid": "manage-student",
    "manage-student": "manage-student",
    "student-registered": "manage-student",
    "student-enrolled": "manage-student",
  };

  if (editPrefer[preferredRoute]) return editPrefer[preferredRoute];
  if (editPrefer[rightsRoute]) return editPrefer[rightsRoute];
  return rightsRoute || preferredRoute;
}

function inferRouteFromContent({ title, type, text, id }) {
  if (
    title.includes("online application") ||
    title.includes("staff application") ||
    title.includes("applicant") ||
    text.includes("online application")
  ) {
    return "staff-application";
  }

  if (title.includes("staff transfer") || text.includes("staff transfer")) {
    return "staff-transfer";
  }

  if (
    title.includes("student leave") ||
    text.includes("student leave request")
  ) {
    return "student-leave-requests";
  }

  if (
    (title.includes("leave request") || text.includes("leave request")) &&
    (type === "staff" || title.includes("staff") || text.includes("staff"))
  ) {
    return "leave-requests";
  }

  if (
    title.includes("leaving") ||
    title.includes("transfer certificate") ||
    text.includes("leaving request")
  ) {
    return "leaving-requests";
  }

  if (
    title.includes("campus transfer") ||
    title.includes("student transfer") ||
    text.includes("campus transfer")
  ) {
    return "student-transfer";
  }

  if (
    title.includes("inquiry") ||
    title.includes("pre-registration") ||
    title.includes("pre registration") ||
    text.includes("pre-registration")
  ) {
    return "manage-inquiry";
  }

  if (title.includes("consent")) {
    return "consent-form";
  }

  if (type === "staff" || title.includes("staff")) {
    return id ? "manage-staff" : "manage-staffgrid";
  }

  if (
    type === "student" ||
    type === "students" ||
    title.includes("student") ||
    title.includes("admission") ||
    title.includes("registration")
  ) {
    return id ? "manage-student" : "manage-studentgrid";
  }

  return null;
}

function upgradeListToEdit(routeName, id, { title, type, text }) {
  if (!id) {
    // Still map applicants list → application form name for consistency
    if (routeName === "applicants") return "staff-application";
    return routeName;
  }

  const map = {
    applicants: "staff-application",
    "staff-application": "staff-application",
    "manage-staffgrid": "manage-staff",
    "manage-staff": "manage-staff",
    "manage-studentgrid": "manage-student",
    "manage-student": "manage-student",
    "student-registered": "manage-student",
    "student-enrolled": "manage-student",
    "student-admission": "manage-student",
    "student-registration": "manage-student",
  };

  if (map[routeName]) return map[routeName];

  // If API sent a generic students/staff type route, prefer edit form
  if (
    (type === "staff" || title.includes("application")) &&
    !map[routeName] &&
    (text.includes("application") || routeName.includes("staff"))
  ) {
    if (text.includes("application") || title.includes("application")) {
      return "staff-application";
    }
  }

  return routeName;
}

function routeAliases(routeName) {
  const aliasMap = {
    "staff-application": ["staff-application", "applicants"],
    applicants: ["applicants", "staff-application"],
    "manage-staff": ["manage-staff", "manage-staffgrid"],
    "manage-staffgrid": ["manage-staffgrid", "manage-staff"],
    "manage-student": [
      "manage-student",
      "manage-studentgrid",
      "student-registered",
      "student-enrolled",
    ],
    "manage-studentgrid": ["manage-studentgrid", "manage-student"],
    "manage-inquiry": [
      "manage-inquiry",
      "inquiry",
      "manage-inquiry-new",
      "inquiry-form",
    ],
    inquiry: ["inquiry", "manage-inquiry", "manage-inquiry-new"],
    "leave-requests": ["leave-requests"],
    "student-leave-requests": ["student-leave-requests"],
    "leaving-requests": ["leaving-requests"],
    "staff-transfer": ["staff-transfer"],
    "student-transfer": ["student-transfer"],
  };

  const list = aliasMap[routeName] || [routeName];
  // Prefer the requested name first
  return [routeName, ...list.filter((r) => r !== routeName)];
}

function buildParams(routeName, id) {
  const params = {};
  const query = {};

  if (!id) return { params, query };

  // Edit forms that take path param :id
  const pathIdRoutes = [
    "staff-application",
    "manage-staff",
    "manage-student",
    "manage-performance",
    "manage-staff-accounts",
  ];

  if (pathIdRoutes.includes(routeName)) {
    params.id = id;
  } else {
    // List pages: pass id in query so they can deep-link later if supported
    query.id = id;
    query.localID = id;
  }

  return { params, query };
}
